import React, { useCallback, useEffect, useReducer, useState } from 'react'
import { EditCycleParametersContent } from '@/dialogs/editCycleParameters/content'
import { useLocalization } from '@/hooks/localization'
import { WhiteBox } from '@/components/Layout'
import uuid from 'uuid'
import { InfoContent, InfoLabel, InfoRow } from '@/components/InfoRow'
import moment from 'moment'
import { Button } from '@/components/button'
import { Input } from '@/components/inputs'

const initialState = { dates: [], weeks: [] }

const editCycleParametersWrappedReducer = (state = { ...initialState }, action) => {
  switch (action.type) {
    case 'addDate':
      if (!state.dates.find(x => x.isSame(action.data, 'day'))) return { ...state, dates: [...state.dates, action.data] }
      break
    case 'removeDate':
      return { ...state, dates: state.dates.filter((v, idx) => idx !== action.data) }
    case 'addWeek':
      if (!state.weeks.find(x => x === action.data)) return { ...state, weeks: [...state.weeks, action.data] }
      break
    case 'removeWeek':
      return { ...state, weeks: state.weeks.filter((v, idx) => idx !== action.data) }
  }

  return state
}

const EditCycleParametersWrapped = ({ loading, saving, onChange, data, id }) => {
  const { localize } = useLocalization()

  const [state, dispatch] = useReducer(editCycleParametersWrappedReducer, data)

  useEffect(() => {
    if (data.weeks.length !== state.weeks.length || data.dates.length !== state.dates.length) onChange({ id, active: true, data: { ...state } })
  }, [id, data, state, onChange])

  const onAddDate = useCallback(date => dispatch({ type: 'addDate', data: date }), [])
  const onRemoveDate = useCallback(index => dispatch({ type: 'removeDate', data: index }), [])
  const onAddWeek = useCallback(week => dispatch({ type: 'addWeek', data: week }), [])
  const onRemoveWeek = useCallback(index => dispatch({ type: 'removeWeek', data: index }), [])

  return <WhiteBox>
    <EditCycleParametersContent
      localize={localize}
      onAddDate={onAddDate}
      onRemoveDateAt={onRemoveDate}
      onWeekAdd={onAddWeek}
      onRemoveWeekAt={onRemoveWeek}
      dates={state.dates}
      weeks={state.weeks}
      loading={loading || saving}
    />
  </WhiteBox>
}

export const EditMultipleCycleParameters = ({ loading, saving, onChange }) => {
  const [dates, setDates] = useState([ { active: true, id: uuid(), data: { ...initialState } } ])
  const [text, setText] = useState('')

  const onTextChange = useCallback((event) => {
    setText(event.target.value)
  }, [])

  const onDateRangeAdd = useCallback(() => {
    let datesArray = []
    const lines = text.split(/\r?\n/)

    if (lines && lines.length) {
      for (let i = 0; i < lines.length; i++) {
        const processedDates = []
        const processedWeeks = []
        const line = lines[i].trim()

        if (line === '') continue

        const dateStrings = line.split(';')

        if (dateStrings && dateStrings.length) {
          for (let j = 0; j < dateStrings.length; j++) {
            const dateString = dateStrings[j].trim()

            if (/\d\d\d\d-\d\d-\d\d/.test(dateString)) {
              processedDates.push(moment(dateString, 'YYYY-MM-DD').toDate())
            } else {
              const parsed = parseInt(dateString)
              if (!isNaN(parsed) && parsed > 0 && parsed < 54) {
                processedWeeks.push(parsed)
              }
            }
          }
        }

        if (processedDates.length || processedWeeks.length) {
          datesArray = [...datesArray, { active: false, id: uuid(), data: { dates: processedDates, weeks: processedWeeks } }]
        }
      }
    }

    setDates(datesArray)
  }, [text])

  const onDateAdd = useCallback(() => {
    const id = uuid()

    setDates([ ...dates.map(x => x.active ? { ...x, active: false } : x), { id, active: true, data: { ...initialState } } ])
  }, [dates, setDates])

  const onDateRemove = useCallback((id) => {
    setDates(dates.filter(x => x.id !== id))
  }, [dates, setDates])

  const onDateChange = useCallback(change => {
    setDates([ ...dates.map(x => x.id === change.id ? { ...x, data: change.data } : x) ])
  }, [dates, setDates])

  const onDateActivate = useCallback(id => setDates(dates.map(x => {
    if (x.active && x.id !== id) return { ...x, active: false }
    if (!x.active && x.id === id) return { ...x, active: true }
    return x
  })), [dates, setDates])

  useEffect(() => onChange(dates.filter(x => x.data.dates.length || x.data.weeks.length).map(x => x.data)),
    [dates, onChange])

  return <React.Fragment>
    <InfoRow>
      <InfoLabel>Manual dates:</InfoLabel>
      <InfoContent>
        <Input type='textarea' onChange={onTextChange} />
        <Button onClick={onDateRangeAdd} icon={'plus'}>Rewrite dates</Button>
      </InfoContent>
    </InfoRow>
    {dates.map(x => !x.active
      ? <WhiteBox>
        <InfoRow>
          <InfoLabel>Dates:</InfoLabel>
          <InfoContent>{x.data.dates.map(d => `${moment(d).format('DD/MM/YYYY')}, `)}</InfoContent>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Weeks:</InfoLabel>
          <InfoContent>{x.data.weeks.map(w => `${w}, `)}</InfoContent>
        </InfoRow>
        <Button onClick={() => onDateActivate(x.id)} iconOnly icon={'pencil'} />
        <Button onClick={() => onDateRemove(x.id)} iconOnly icon={'x'} />
      </WhiteBox>
      : <EditCycleParametersWrapped id={x.id} data={x.data} onChange={onDateChange} loading={loading} saving={saving} />)}
    <Button onClick={onDateAdd} iconOnly icon={'plus'} />
  </React.Fragment>
}



// WEBPACK FOOTER //
// ./src/dialogs/editCycleParameters/editMultipleCycleParameters.js